import { FetchRequest, JsonRpcProvider } from 'ethers';

const ETH_RPC_FALLBACK_URLS = [
  'https://ethereum-rpc.publicnode.com',
  'https://1rpc.io/eth',
  'https://eth.drpc.org',
  // eth.llamarpc.com retiré le 2026-09-10 — renvoyait "error code: 525" (échec handshake TLS
  // Cloudflare) de façon persistante, ne servait que de trou dans le repli.
];

const BSC_RPC_FALLBACK_URLS = [
  'https://bsc-dataseed.binance.org/',
  'https://bsc-rpc.publicnode.com',
  'https://1rpc.io/bnb',
];

// Arbitrum / Base (2026-09-10, signalé par l'utilisateur : "sur Arbitrum et Base le user est
// débité mais la page reste figée") — arbitrum-one-rpc.publicnode.com et base-rpc.publicnode.com
// renvoient 403 Forbidden aux requêtes ORIGINE NAVIGATEUR (constaté dans la console : "POST
// https://arbitrum-one-rpc.publicnode.com/ 403"). La confirmation on-chain du paiement
// (getTransactionReceipt) échouait donc en boucle → confirmPayment jamais appelé → page bloquée
// alors que la tx était bien passée. Endpoints officiels/LlamaRPC en tête, publicnode en dernier
// recours (fonctionne encore côté serveur, garde une chance si les autres tombent).
const ARBITRUM_RPC_FALLBACK_URLS = [
  'https://arb1.arbitrum.io/rpc',
  'https://1rpc.io/arb',
  'https://arbitrum-one-rpc.publicnode.com',
];

const BASE_RPC_FALLBACK_URLS = [
  'https://mainnet.base.org',
  'https://1rpc.io/base',
  'https://base-rpc.publicnode.com',
];

// Ethers donne 300_000ms (5 min) de timeout par défaut à chaque requête HTTP — largement au-delà
// de ce qu'un utilisateur tolère, et surtout ça neutralise le repli multi-endpoint ci-dessous : un
// 1er endpoint qui ne répond pas (accroc réseau mobile, WebView Telegram en arrière-plan) bloque
// jusqu'à 5 min avant même d'essayer le 2e. Un budget court par tentative fait échouer/rejeter vite
// pour laisser le `for` de withRpcFallback() passer au endpoint suivant en quelques secondes.
const RPC_TIMEOUT_MS = 15_000;

// Même cause racine que sur earn.iristoken.io / earnbsc.iristoken.io / iris-p2p (BSC) :
// l'endpoint public unique utilisé jusqu'ici pour les lectures/confirmations on-chain a des pannes
// intermittentes. Sans repli, ces appels échouaient silencieusement (souvent absorbées par un
// `catch` renvoyant 0/false en amont) ou restaient bloqués indéfiniment (tx.wait() sans jamais se
// résoudre), donnant l'impression d'un solde/statut "disparu" ou d'une action bloquée pour toujours
// alors que les fonds/la transaction étaient intacts on-chain.
function makeRpcFallback(urls: string[]) {
  const providerCache = new Map<string, JsonRpcProvider>();

  function providerForAttempt(attempt: number): JsonRpcProvider {
    const url = urls[Math.min(attempt, urls.length - 1)];
    let provider = providerCache.get(url);
    if (!provider) {
      const request = new FetchRequest(url);
      request.timeout = RPC_TIMEOUT_MS;
      provider = new JsonRpcProvider(request);
      providerCache.set(url, provider);
    }
    return provider;
  }

  return async function withRpcFallback<T>(fn: (provider: JsonRpcProvider) => Promise<T>): Promise<T> {
    let lastErr: any;
    for (let attempt = 0; attempt < urls.length; attempt++) {
      try {
        return await fn(providerForAttempt(attempt));
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr;
  };
}

export const withEthRpcFallback = makeRpcFallback(ETH_RPC_FALLBACK_URLS);
export const withBscRpcFallback = makeRpcFallback(BSC_RPC_FALLBACK_URLS);
export const withArbitrumRpcFallback = makeRpcFallback(ARBITRUM_RPC_FALLBACK_URLS);
export const withBaseRpcFallback = makeRpcFallback(BASE_RPC_FALLBACK_URLS);

// Repli générique pour une chaîne sans liste dédiée (RobinhoodChain) — garde la même signature
// que les `withXxxRpcFallback` ci-dessus pour être utilisable de façon uniforme.
export function makeSingleRpc(url: string) {
  return makeRpcFallback([url]);
}
